import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { ModalBase } from '@components/Modal';
import { MultiSelect } from '@components/MultiSelect';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { knowledgeBaseService } from '@services';
import { handleApiError } from '@helpers/api';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 564,
        '@media (max-width: 1024px)': {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            maxHeight: 'none',
        },
    },
    '& .modal-base-content': {
        display: 'grid',
        gridGap: 16,
        padding: 16,
    },
});

const BulkAssignModal = ({ isOpen, onClose, selectedItems, totalCount, currentQuery, onAssigned }) => {
    const { t } = useTranslation();
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const agents = useSelector(state => state.agents.data);

    const agentsOptions = useMemo(
        () => agents.map(agent => ({ value: agent.id, label: agent.name, name: agent.name })),
        [agents],
    );

    const selectedCount = selectedItems.all ? totalCount : Object.keys(selectedItems.list).length;

    const onSubmit = async () => {
        try {
            setIsSubmitting(true);
            await knowledgeBaseService.assignAgents({
                items: Object.keys(selectedItems.list),
                allItems: selectedItems.all,
                filter: currentQuery,
                agents: selectedAgents,
            });
            await onAssigned();
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onClose}
            title={t('buttons.assignAgents')}
            onPrimaryAction={onSubmit}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !selectedAgents.length}
        >
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {t('KnowledgeBase.itemsSelected', { count: selectedCount })}
            </Typography>
            <MultiSelect
                value={selectedAgents}
                onChange={setSelectedAgents}
                options={agentsOptions}
                label={t('KnowledgeBase.agents')}
                fullWidth
                countable
                nameBased
                required
            />
        </Modal>
    );
};

export default BulkAssignModal;
